/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRef, useState, useEffect, useMemo } from "react";
import "./App.scss";
// Old monolithic system (kept for reference):
// import { LiveAPIProvider, useLiveAPIContext } from "./contexts/LiveAPIContext";
// import { Altair } from "./components/altair/Altair";

// New multi-agent system:
import { AgentProvider } from "./contexts/AgentContext";
import { useAgentContext } from "./contexts/AgentContext";
import { useTranscript } from "./contexts/TranscriptContext";
import { AltairAgent } from "./components/altair/AltairAgent";

import { ThemeProvider } from "./contexts/ThemeContext";
import { TranscriptProvider } from "./contexts/TranscriptContext";
import ControlTray from "./components/control-tray/ControlTray";
import SplashScreen from "./components/splash-screen/SplashScreen";
import LoginScreen from "./components/login/LoginScreen";
import WelcomeMessage from "./components/welcome-message/WelcomeMessage";
import { TranscriptPanel } from "./components/transcript-panel/TranscriptPanel";
import { TranscriptToggle } from "./components/transcript-toggle/TranscriptToggle";
import { AllPermissions } from "./utils/permissions";
import cn from "classnames";
import { logger } from "./utils/logger";
import { config } from "./config/runtime-config";

// ============================================================================
// Authentication Configuration - from runtime config
// ============================================================================
const BACKEND_URL = config.REACT_APP_BACKEND_URL;
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

// Validate authentication configuration
if (!BACKEND_URL && !API_KEY) {
  console.error("❌ No authentication configured. Set REACT_APP_BACKEND_URL (in config.js) or REACT_APP_GEMINI_API_KEY");
  throw new Error("Missing authentication configuration: Set REACT_APP_BACKEND_URL in config.js or REACT_APP_GEMINI_API_KEY in .env file");
}

// Use ephemeral tokens if backend URL is configured (recommended)
const USE_EPHEMERAL_TOKENS = !!BACKEND_URL;

if (USE_EPHEMERAL_TOKENS) {
  logger.connection("🔒 Using EPHEMERAL TOKEN mode (secure) - Backend:", BACKEND_URL);
} else if (API_KEY) {
  logger.connection("⚠️ Using API KEY mode (legacy/development) - Consider using ephemeral tokens for production");
}

/**
 * Pre-establish WebSocket connection on page load for INSTANT streaming
 * Connection is ready but audio doesn't start until user clicks "Connect"
 * This happens in the background while user is viewing splash/login screens
 */
if (USE_EPHEMERAL_TOKENS && BACKEND_URL) {
  // Get voice gender from stored preference or default
  const storedVoiceGender = (localStorage.getItem('saaed_voice_gender') as 'male' | 'female') || 'male';
  
  // Pre-establish WebSocket connection asynchronously
  import('./lib/token-service').then(({ prefetchInitialConnection }) => {
    prefetchInitialConnection(BACKEND_URL, storedVoiceGender);
  }).catch(error => {
    console.warn('Failed to pre-establish WebSocket connection:', error);
    // Non-critical - app will establish connection normally when user clicks connect
  });
}

/**
 * Pre-load FAQ data on app startup for instant FAQ responses
 * FAQs are loaded from backend API and cached in memory
 * This happens in the background during splash/login screens
 */
import('./utils/faq-search').then(({ loadFAQs }) => {
  loadFAQs().then(faqs => {
    logger.info(`✅ Pre-loaded ${faqs.length} FAQs from backend`);
  }).catch(error => {
    logger.warn('⚠️ Failed to pre-load FAQs:', error);
    // Non-critical - FAQs will be loaded on first FAQ agent usage
  });
}).catch(error => {
  logger.warn('⚠️ Failed to import FAQ loader:', error);
});

// ============================================================================
// Embedded Mode Detection - for iframe integration
// ============================================================================
/**
 * Parse URL parameters for embedded mode and deep linking
 * 
 * Example URLs:
 * - ?embedded=true - Launch in embedded mode
 * - ?embedded=true&agent=accident_start_agent&lang=en - Jump to specific flow
 * - ?embedded=true&context={"key":"value"} - Pass context from parent
 */
function parseEmbeddedParams(): {
  isEmbedded: boolean;
  initialAgent?: string;
  initialContext?: Record<string, any>;
  voiceGender?: 'male' | 'female';
  autoConnect?: boolean;
} {
  const params = new URLSearchParams(window.location.search);
  
  const isEmbedded = params.get('embedded') === 'true';
  const initialAgent = params.get('agent') || undefined;
  const voiceGender = (params.get('voice') as 'male' | 'female') || undefined;
  const autoConnect = params.get('autoConnect') === 'true';
  
  // Parse context if provided
  let initialContext: Record<string, any> | undefined;
  const contextParam = params.get('context');
  if (contextParam) {
    try {
      initialContext = JSON.parse(decodeURIComponent(contextParam));
    } catch (e) {
      console.warn('[Embedded] Failed to parse context parameter:', e);
    }
  }
  
  // Build context from individual params if not provided as JSON
  if (!initialContext && (params.get('lang') || params.get('username'))) {
    initialContext = {};
    
    // Extract common context fields from URL
    if (params.get('lang')) {
      initialContext.language = params.get('lang');
      initialContext.contextLanguage = params.get('lang');
    }
    if (params.get('username')) initialContext.username = params.get('username');
    if (params.get('phone')) initialContext.phone = params.get('phone');
    if (params.get('report_number')) initialContext.report_number = params.get('report_number');
  }
  
  if (isEmbedded) {
    logger.info('[Embedded] Mode detected:', { initialAgent, initialContext, voiceGender, autoConnect });
  }
  
  return { isEmbedded, initialAgent, initialContext, voiceGender, autoConnect };
}

// Inner component that can use TranscriptContext
function MainAppContent({ 
  userData, 
  permissions,
  voiceGender,
  onLogout,
  isEmbedded = false,
  initialAgent,
  initialContext,
  autoConnect = false
}: { 
  userData: {username: string; mobileNumber: string} | null;
  permissions: AllPermissions | null;
  voiceGender: 'male' | 'female';
  onLogout: () => void;
  isEmbedded?: boolean;
  initialAgent?: string;
  initialContext?: Record<string, any>;
  autoConnect?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const { disconnect, stopAudio } = useAgentContext();
  const { clearTranscript } = useTranscript();
  
  // Clear transcript when user logs out (userData becomes null)
  const prevUserDataRef = useRef(userData);
  useEffect(() => {
    if (prevUserDataRef.current && !userData) {
      // User logged out
      clearTranscript();
    }
    prevUserDataRef.current = userData;
  }, [userData, clearTranscript]);

  const handleLogout = async () => {
    // Stop audio streaming immediately (first priority)
    stopAudio();
    
    // Stop any active video streams
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
    }
    
    // Clear video element
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    // Clear transcript from localStorage
    localStorage.removeItem('saaed_transcript');
    localStorage.removeItem('saaed_transcript_panel_open');
    
    // Disconnect the LiveAPI session
    await disconnect();
    
    // Then call the parent logout handler
    onLogout();
  };

  return (
    <div 
      className="streaming-console"
      style={{
        // Set gender splash as background during connection
        backgroundImage: voiceGender === 'male' 
          ? 'url(/splash_male.png)' 
          : 'url(/splash_female.png)',
        backgroundSize: 'cover', // Changed from '100% 100%' to 'cover' to maintain aspect ratio
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <main>
        <div className="main-app-area">
          {/* Multi-Agent System */}
          <AltairAgent 
            permissions={permissions} 
            userData={userData} 
            voiceGender={voiceGender} 
            onLogout={handleLogout} 
            videoStream={videoStream}
            embeddedMode={isEmbedded}
            initialAgent={initialAgent}
            initialContext={initialContext}
          />
          <video
            className={cn("stream", {
              hidden: !videoRef.current || !videoStream,
            })}
            ref={videoRef}
            autoPlay
            playsInline
          />
        </div>

        <ControlTray
          videoRef={videoRef}
          supportsVideo={true}
          onVideoStreamChange={setVideoStream}
        >
          {/* Logout button */}
          {userData && (
            <button
              className="action-button logout-button"
              onClick={handleLogout}
              title={`Logout (${userData.username})`}
            >
              <span className="material-symbols-outlined">logout</span>
            </button>
          )}
        </ControlTray>
      </main>
      
      {/* Transcript components - outside main */}
      <TranscriptToggle />
      <TranscriptPanel />
    </div>
  );
}

// Wrapper component that provides TranscriptContext
function MainApp(props: { 
  userData: {username: string; mobileNumber: string} | null;
  permissions: AllPermissions | null;
  voiceGender: 'male' | 'female';
  onLogout: () => void;
  isEmbedded?: boolean;
  initialAgent?: string;
  initialContext?: Record<string, any>;
  autoConnect?: boolean;
}) {
  return (
    <TranscriptProvider>
      <MainAppContent {...props} />
    </TranscriptProvider>
  );
}

function App() {
  // Parse embedded mode parameters
  const embeddedParams = useMemo(() => parseEmbeddedParams(), []);
  const { isEmbedded, initialAgent, initialContext, autoConnect } = embeddedParams;
  
  // Unique session ID for proper multi-user isolation
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  // Splash screen state (skip in embedded mode)
  const [showSplash, setShowSplash] = useState<boolean>(!isEmbedded);
  // Login screen state (skip in embedded mode)
  const [showLogin, setShowLogin] = useState<boolean>(false);
  // Welcome message state (skip in embedded mode)
  const [showWelcome, setShowWelcome] = useState<boolean>(false);
  // Gender-specific splash state (shown after voice selection, skip in embedded mode)
  const [showGenderSplash, setShowGenderSplash] = useState<boolean>(false);
  // Permissions state
  const [permissions, setPermissions] = useState<AllPermissions | null>(null);
  // User data state
  const [userData, setUserData] = useState<{username: string; mobileNumber: string} | null>(null);
  // Voice gender preference (from URL params in embedded mode, or stored preference)
  const [voiceGender, setVoiceGender] = useState<'male' | 'female'>(() => {
    if (isEmbedded && embeddedParams.voiceGender) {
      return embeddedParams.voiceGender;
    }
    return (localStorage.getItem('saaed_voice_gender') as 'male' | 'female') || 'male';
  });

  useEffect(() => {
    // Check if user is already logged in
    const savedUserData = localStorage.getItem('saaed_user_data');
    if (savedUserData) {
      try {
        const parsedUserData = JSON.parse(savedUserData);
        setUserData(parsedUserData);
      } catch (error) {
        console.error('Failed to parse saved user data:', error);
        localStorage.removeItem('saaed_user_data');
      }
    }
  }, []);

  // Handle embedded mode initialization
  useEffect(() => {
    if (isEmbedded) {
      logger.info('[Embedded] Initializing embedded mode');
      
      // Set default permissions for embedded mode (all granted)
      setPermissions({
        location: { granted: true },
        microphone: { granted: true },
        camera: { granted: true }
      });
      
      // Use context user data if provided, otherwise use placeholder
      if (initialContext?.username && initialContext?.phone) {
        setUserData({
          username: initialContext.username,
          mobileNumber: initialContext.phone
        });
      } else if (!userData) {
        // Placeholder for embedded mode if no user data provided
        setUserData({
          username: 'Guest',
          mobileNumber: ''
        });
      }
      
      logger.info('[Embedded] Embedded mode initialized');
    }
  }, [isEmbedded, initialContext, userData]);

  // Handle splash screen completion
  const handleSplashComplete = () => {
    setShowSplash(false);
    // If user is already logged in, skip login screen
    if (userData) {
      setShowWelcome(true);
    } else {
      setShowLogin(true);
    }
  };

  // Handle login completion
  const handleLoginComplete = (loginData: {username: string; mobileNumber: string}) => {
    setUserData(loginData);
    setShowLogin(false);
    setShowWelcome(true);
  };

  // Handle welcome message completion with permissions
  const handleWelcomeComplete = (grantedPermissions: AllPermissions, selectedVoiceGender: 'male' | 'female') => {
    console.log(`📋 [APP] Welcome complete - Selected voice: ${selectedVoiceGender}`);
    setPermissions(grantedPermissions);
    setVoiceGender(selectedVoiceGender);
    
    // Check if gender changed from what was initially cached
    const previousGender = (localStorage.getItem('saaed_voice_gender') as 'male' | 'female') || 'male';
    
    // Store new voice preference
    localStorage.setItem('saaed_voice_gender', selectedVoiceGender);
    
    console.log(`🎤 [APP] Voice gender selected: ${selectedVoiceGender}`);
    
    // Only pre-establish connection if gender changed from what was initially cached
    if (USE_EPHEMERAL_TOKENS && BACKEND_URL && selectedVoiceGender !== previousGender) {
      console.log(`🔄 [APP] Gender changed from ${previousGender} to ${selectedVoiceGender}, re-establishing connection`);
      import('./lib/token-service').then(({ prefetchInitialConnection }) => {
        prefetchInitialConnection(BACKEND_URL, selectedVoiceGender);
      }).catch(error => {
        console.warn('Failed to pre-establish connection for voice gender:', error);
      });
    } else {
      console.log(`✅ [APP] WebSocket already pre-connected for gender: ${selectedVoiceGender}`);
    }
    
    setShowWelcome(false);
    console.log(`🖼️ [APP] Setting showGenderSplash to TRUE`);
    // Show gender-specific splash screen
    setShowGenderSplash(true);
  };
  
  // Handle gender splash completion
  const handleGenderSplashComplete = () => {
    console.log(`✅ [APP] Gender splash complete - hiding gender splash`);
    setShowGenderSplash(false);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('saaed_user_data');
    setUserData(null);
    setPermissions(null);
    setShowLogin(true);
  };

  return (
    <ThemeProvider>
      <div className="App" style={{ 
        // Prevent white flash between splashes
        backgroundColor: '#000000',
        minHeight: '100vh'
      }}>
        {(() => {
          console.log(`🎭 [APP] Render state:`, {
            showSplash,
            showLogin,
            showWelcome,
            showGenderSplash,
            voiceGender,
            shouldShowMainApp: !showSplash && !showLogin && !showWelcome && !showGenderSplash
          });
          return null;
        })()}
        
        {/* Initial Splash Screen (default splash.png) */}
        {showSplash && (
          <SplashScreen key="default-splash" onComplete={handleSplashComplete} gender={null} />
        )}
        
        {/* Login Screen */}
        {showLogin && (
          <LoginScreen onComplete={handleLoginComplete} />
        )}
        
        {/* Welcome Message */}
        {showWelcome && (
          <WelcomeMessage onComplete={handleWelcomeComplete} />
        )}
        
        {/* Gender-Specific Splash Screen (splash_male.png or splash_female.png) */}
        {showGenderSplash && (
          <SplashScreen key={`gender-splash-${voiceGender}`} onComplete={handleGenderSplashComplete} gender={voiceGender} />
        )}
        
        {/* Only show AgentProvider and MainApp after all splashes and welcome are complete OR in embedded mode */}
        {((!showSplash && !showLogin && !showWelcome && !showGenderSplash) || isEmbedded) && (
          <AgentProvider 
            key={sessionId} 
            backendUrl={USE_EPHEMERAL_TOKENS ? BACKEND_URL : undefined}
            apiKey={USE_EPHEMERAL_TOKENS ? undefined : API_KEY}
          >
            {(() => {
              logger.connection("AgentProvider initialized with session:", sessionId);
              logger.connection("Auth mode:", USE_EPHEMERAL_TOKENS ? "Ephemeral Tokens" : "API Key");
              return null;
            })()}
            <MainApp 
              userData={userData}
              permissions={permissions}
              voiceGender={voiceGender}
              onLogout={handleLogout}
              isEmbedded={isEmbedded}
              initialAgent={initialAgent}
              initialContext={initialContext}
              autoConnect={autoConnect}
            />
          </AgentProvider>
        )}
      </div>
    </ThemeProvider>
  );
}

export default App;
